import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import SEOHead from '../components/SEOHead';
import PublicPageShell from '../components/landing/PublicPageShell';
import { Package, Plane, Users, CreditCard, Settings, Mail, Search, Shield } from 'lucide-react';

// Titre de rubrique : filet fin et petite icône monochrome, sans aplat de couleur.
const Section = ({ icon: Icon, title, children }) => (
  <section className="landing-help-section">
    <h2>
      <Icon size={17} strokeWidth={1.75} aria-hidden="true" />
      {title}
    </h2>
    <div className="landing-help-body">{children}</div>
  </section>
);

const FAQItem = ({ q, a }) => (
  <details className="landing-faq">
    <summary>
      {q}
      <span aria-hidden="true">+</span>
    </summary>
    <p>{a}</p>
  </details>
);

const HelpCenter = () => {
  const { language, t } = useLanguage();
  const isFr = language === 'fr';

  return (
    <PublicPageShell
      eyebrow={t('footerSupport')}
      title={t('helpCenter')}
      lede={t('findAnswers')}
    >
      <SEOHead
        title={t('seoHelpTitle')}
        description={t('seoHelpDesc')}
        canonical="/help"
      />
      <div className="landing-prose" style={{ maxWidth: '820px' }}>

        <Section icon={Package} title={t('helpPackageManagement')}>
          <div className="space-y-3">
            <FAQItem
              q={isFr ? 'Comment créer un colis ?' : 'How to create a package?'}
              a={isFr
                ? 'Depuis le tableau de bord partenaire, cliquez "Créer un colis". Remplissez les informations expéditeur, destinataire, poids et destination. Le code OCE est généré automatiquement.'
                : 'From the partner dashboard, click "Create package". Fill in sender, recipient, weight and destination info. The OCE code is generated automatically.'}
            />
            <FAQItem
              q={isFr ? 'Comment suivre un colis ?' : 'How to track a package?'}
              a={isFr
                ? 'Rendez-vous sur la page de suivi (/track) et entrez le code OCE du colis. Vous verrez le statut en temps réel et l\'historique complet.'
                : 'Go to the tracking page (/track) and enter the package OCE code. You\'ll see real-time status and full history.'}
            />
            <FAQItem
              q={isFr ? 'Quels sont les statuts possibles ?' : 'What are the possible statuses?'}
              a={isFr
                ? 'Enregistré → Collecté → En transit → Arrivé → En douane → Dédouané → En livraison → Livré. Chaque transition est tracée avec horodatage.'
                : 'Registered → Collected → In transit → Arrived → Customs → Cleared → Out for delivery → Delivered. Each transition is timestamped.'}
            />
          </div>
        </Section>

        <Section icon={Plane} title={t('helpShipments')}>
          <div className="space-y-3">
            <FAQItem
              q={isFr ? 'Comment créer un départ ?' : 'How to create a shipment?'}
              a={isFr
                ? 'Depuis la page Départs, cliquez "Nouveau Départ". Renseignez le nom, le vol/navire, les pays d\'origine et destination, et les dates. Les colis sont ensuite assignés au départ.'
                : 'From the Shipments page, click "New Departure". Enter name, flight/vessel, origin and destination countries, and dates. Packages are then assigned to the shipment.'}
            />
            <FAQItem
              q={isFr ? 'Fret aérien ou maritime ?' : 'Air or maritime freight?'}
              a={isFr
                ? 'Selon la configuration de votre entreprise, vous pouvez gérer du fret aérien, maritime ou les deux. Le mode de transport se sélectionne lors de la création du colis et du départ.'
                : 'Depending on your company setup, you can handle air freight, maritime freight, or both. Transport mode is selected when creating packages and shipments.'}
            />
          </div>
        </Section>

        <Section icon={Users} title={t('helpRolesUsers')}>
          <div className="space-y-3">
            <FAQItem
              q={isFr ? 'Quels sont les rôles disponibles ?' : 'What roles are available?'}
              a={isFr
                ? 'Admin Entreprise (gestion complète), Admin Pays (gestion par pays), Collecteur (création de colis), Livreur (livraison locale), Client (suivi uniquement).'
                : 'Company Admin (full management), Country Admin (per-country management), Collector (package creation), Delivery Partner (local delivery), Client (tracking only).'}
            />
            <FAQItem
              q={isFr ? 'Comment ajouter un utilisateur ?' : 'How to add a user?'}
              a={isFr
                ? 'Depuis la page Gestion des utilisateurs, cliquez "Ajouter un utilisateur". Attribuez un rôle, un pays et une ville si nécessaire.'
                : 'From User Management page, click "Add user". Assign a role, country and city if needed.'}
            />
          </div>
        </Section>

        <Section icon={CreditCard} title={t('helpBilling')}>
          <div className="space-y-3">
            <FAQItem
              q={isFr ? 'Comment fonctionne la facturation ?' : 'How does billing work?'}
              a={isFr
                ? 'Les factures sont auto-générées à la création de chaque colis selon la tarification configurée (prix/kg par route + suppléments catégorie). Les statuts de paiement sont : réglé, non réglé, avancé.'
                : 'Invoices are auto-generated when packages are created based on configured pricing (price/kg per route + category supplements). Payment statuses: paid, unpaid, advance.'}
            />
          </div>
        </Section>

        <Section icon={Settings} title={t('helpConfiguration')}>
          <div className="space-y-3">
            <FAQItem
              q={isFr ? 'Comment configurer mon entreprise ?' : 'How to configure my company?'}
              a={isFr
                ? 'Rendez-vous dans Paramètres > Entreprise. Vous y trouverez les onglets : Général (nom, couleurs, pays), Transport (modes, fréquence, compagnie), Communication (email, WhatsApp), Financier (tarifs, devises).'
                : 'Go to Settings > Company. You\'ll find tabs: General (name, colors, countries), Transport (modes, frequency, airline), Communication (email, WhatsApp), Financial (pricing, currencies).'}
            />
          </div>
        </Section>

        <Section icon={Mail} title={t('helpNeedMore')}>
          <div className="mt-3 rounded-xl border p-4" style={{ borderColor: 'hsl(var(--accent) / 0.3)', background: 'hsl(var(--accent) / 0.05)' }}>
            <p className="font-semibold" style={{ color: 'hsl(var(--foreground))' }}>
              {t('contactSupport')}
            </p>
            <p className="mt-1">Email : support@fretmanager.com</p>
            <p>{t('responseTime24h')}</p>
          </div>
        </Section>

      </div>
    </PublicPageShell>
  );
};

export default HelpCenter;
